// ===========================================
// Twilio Tab
// SMS notification settings (WhatsApp is now separate via Meta Cloud API)
// ===========================================

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from '@mui/material';
import {
  Sms as SmsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  useIntegrationSettings,
  useUpdateIntegrationSettings,
  useIntegrationOverview,
  useTwilioStats,
} from '../../../hooks/useIntegrations';
import IntegrationKeyManager from './IntegrationKeyManager';
import SetupGuideCard from './SetupGuideCard';
import { twilioSetupSteps, getWebhookUrl, docsLinks } from './setupGuides';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function TwilioTab() {
  const { data: settings, isLoading: settingsLoading } = useIntegrationSettings();
  const { data: overview, isLoading: overviewLoading } = useIntegrationOverview();
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useTwilioStats();
  const updateSettings = useUpdateIntegrationSettings();

  const twilioSettings = settings?.twilio;
  const twilioOverview = overview?.twilio;

  const handleToggleEnabled = async () => {
    if (!twilioSettings) return;
    await updateSettings.mutateAsync({
      twilio: { enabled: !twilioSettings.enabled },
    });
  };

  const handleToggleSms = async () => {
    if (!twilioSettings) return;
    await updateSettings.mutateAsync({
      twilio: { smsEnabled: !twilioSettings.smsEnabled },
    });
  };

  if (settingsLoading || overviewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!twilioSettings || !twilioOverview) {
    return <Alert severity="error">Failed to load Twilio settings.</Alert>;
  }

  const getStatusChip = (status: string) => {
    switch (status.toUpperCase()) {
      case 'DELIVERED':
        return <Chip label="Delivered" size="small" color="success" />;
      case 'SENT':
        return <Chip label="Sent" size="small" color="primary" />;
      case 'FAILED':
        return <Chip label="Failed" size="small" color="error" />;
      case 'PENDING':
        return <Chip label="Pending" size="small" color="warning" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Send SMS notifications to parents for lesson reminders, payment confirmations,
        and other important updates.
      </Typography>

      {/* Setup Guide */}
      <SetupGuideCard
        integration="twilio"
        steps={twilioSetupSteps}
        webhookUrl={getWebhookUrl('twilio')}
        docsLink={docsLinks.twilio}
      />

      {/* Per-School API Key Management */}
      <IntegrationKeyManager integration="twilio" />

      {/* Master Enable/Disable */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<SmsIcon color={twilioSettings.enabled ? 'primary' : 'disabled'} />}
          title="SMS Notifications"
          action={
            <Switch
              checked={twilioSettings.enabled}
              onChange={handleToggleEnabled}
              disabled={updateSettings.isPending || !twilioOverview.isConfigured}
            />
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Enable or disable SMS notifications for your school.
          </Typography>
        </CardContent>
      </Card>

      {/* Channel Settings */}
      {twilioSettings.enabled && twilioOverview.isConfigured && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="SMS Channel" />
          <CardContent>
            <Stack spacing={2}>
              {/* SMS Toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SmsIcon color={twilioSettings.smsEnabled ? 'primary' : 'disabled'} />
                  <Box>
                    <Typography variant="subtitle2">SMS Notifications</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Send text messages to mobile phones
                    </Typography>
                  </Box>
                </Stack>
                <Switch
                  checked={twilioSettings.smsEnabled}
                  onChange={handleToggleSms}
                  disabled={updateSettings.isPending}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Stats Card */}
      {twilioSettings.enabled && twilioOverview.isConfigured && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="SMS Statistics"
            action={
              <Button
                startIcon={statsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => refetchStats()}
                disabled={statsLoading}
                size="small"
              >
                Refresh
              </Button>
            }
          />
          <CardContent>
            <Stack spacing={3}>
              {/* Summary Stats */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Summary
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography variant="h4">{twilioOverview.messagesSentToday}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Messages Today
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4">{twilioOverview.messagesSentThisMonth}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      This Month
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4">{twilioOverview.deliverySuccessRate}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Delivery Rate
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Delivery by Type */}
              {stats?.deliveryByType && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Delivery by Notification Type
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Sent</TableCell>
                          <TableCell align="right">Delivered</TableCell>
                          <TableCell align="right">Failed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.deliveryByType.map((row) => (
                          <TableRow key={row.type}>
                            <TableCell>{row.type.replace(/_/g, ' ')}</TableCell>
                            <TableCell align="right">{row.sent}</TableCell>
                            <TableCell align="right">{row.delivered}</TableCell>
                            <TableCell align="right">
                              {row.failed > 0 ? (
                                <Chip label={row.failed} size="small" color="error" />
                              ) : (
                                0
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Divider />

              {/* Recent Messages */}
              {stats?.recentMessages && stats.recentMessages.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Recent SMS Messages
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Recipient</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Sent</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.recentMessages.slice(0, 10).map((msg) => (
                          <TableRow key={msg.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {msg.recipientPhone
                                  ? msg.recipientPhone.slice(0, 4) + '****' + msg.recipientPhone.slice(-3)
                                  : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>{msg.type.replace(/_/g, ' ')}</TableCell>
                            <TableCell>{getStatusChip(msg.status)}</TableCell>
                            <TableCell>
                              {new Date(msg.sentAt).toLocaleString('en-AU', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
